import { Button, Modal } from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { ErrorText } from '../../components/ErrorText/ErrorText'
import { post } from '../../utils/api'
import { useStyles } from './BulkUploadButton.styles'

// The header bulk_upload checks for, so a filled-in template is accepted as
// is. Built in the browser: no endpoint just to serve a fixed line.
const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(
  'Minsan;Macchinabile\n'
)}`

type RowError = { row: number; minsan: string; detail: string }

type BulkUploadResult = {
  updated: number
}

// The upload is all-or-nothing: any bad row refuses the whole file, so the
// per-row detail arrives on a 400 rather than alongside a partial success.
type BulkUploadError = {
  response?: { data?: { error?: string; errors?: RowError[] } }
}

export const BulkUploadButton = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [modal, setModal] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const queryClient = useQueryClient()

  const { mutate, isLoading, data, error, reset } = useMutation(
    ['drug-registry', 'bulk-upload'],
    (uploadedFile: File) => {
      const formData = new FormData()
      formData.append('file', uploadedFile)
      return post<BulkUploadResult>(
        '/drug-registry/bulk-upload/',
        formData,
        undefined,
        true
      )
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(['drug-registry'])
      },
    }
  )

  const rowErrors = (error as BulkUploadError)?.response?.data?.errors ?? []

  return (
    <>
      <Button
        label={t('Upload CSV')}
        type="action"
        onClick={() => setModal(true)}
      />
      {modal && (
        <Modal
          title={t('Upload CSV')}
          onCloseRequest={() => {
            setModal(false)
            setFile(null)
            reset()
          }}
          footer={
            <Button
              label={t('Apply')}
              type="action"
              // `loading` only swaps the button icon — it does not set the
              // DOM `disabled` attribute — so without `isLoading` here a
              // double click would fire a second upsert whose all-"updated"
              // response lands last and overwrites the displayed summary.
              disabled={!file || isLoading}
              loading={isLoading}
              onClick={() => file && mutate(file)}
            />
          }
        >
          <>
            <p>
              {t(
                'CSV file, ";"-separated, with columns Minsan;Macchinabile (values TRUE/FALSE). Example: {{ example }}',
                { example: '012345678;TRUE' }
              )}
            </p>
            <p>
              <a
                href={TEMPLATE_HREF}
                download="template-anagrafica-farmaci.csv"
              >
                {t('Download template')}
              </a>
            </p>
            <input
              aria-label={t('Upload CSV file')}
              type="file"
              accept=".csv"
              // The modal stays open after an upload so the user can read the
              // per-row errors, fix the CSV and retry with the same filename.
              // A file input does not re-fire `onChange` when the picked file's
              // identity is unchanged, so clear the value on every click.
              onClick={(e) => {
                const input = e.target as HTMLInputElement
                input.value = ''
              }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {data && (
              <div role="status">
                <p>{t('{{ updated }} updated', { updated: data.updated })}</p>
              </div>
            )}
            {error && (
              // `ErrorText` renders a plain <p> and takes no `role` prop, so
              // the live region goes on a wrapper around it.
              <div role="alert" className={classes.scrollableContent}>
                <ErrorText>
                  {(error as BulkUploadError)?.response?.data?.error ??
                    t('Could not upload the file')}
                </ErrorText>
                {rowErrors.length > 0 && (
                  <>
                    {/* The whole file was refused, so the user has to fix
                        every line before any of it lands — hence the count
                        up front and the full list below it, never a
                        truncated one. */}
                    <p>
                      {t(
                        'The file was not applied: {{ count }} rows contain errors',
                        {
                          count: rowErrors.length,
                        }
                      )}
                    </p>
                    <ul>
                      {rowErrors.map((rowError) => (
                        <li key={rowError.row}>
                          {t(
                            'Row {{ row }} ({{ minsan }}): {{ detail }}',
                            rowError
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </>
        </Modal>
      )}
    </>
  )
}
